import { ISMSRepositoryType } from "../../adapters/driven/repositories/smsRepository";

export const SMSServiceImplFactory = ({
  smsRepository,
}: {
  smsRepository: ISMSRepositoryType;
}): ISMSService => {
  const sendOTP: ISendOTPService = async (payload) => {
    try {
      const response = await smsRepository.sendOTP({
        phoneNumber: payload.phone_number,
      });

      return {
        success: response.status,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const verifyOTP: IVerifyOTPService = async (payload) => {
    try {
      const response = await smsRepository.verifyOTP({
        uniqueRequestId: payload.uniqueRequestId,
        otp: payload.otp,
      });
      return {
        success: response.message === "OTP Verified!",
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const savePhoneNumber: ISavePhoneNumberService = async (payload) => {
    try {
      const response = await smsRepository.saveNumber({
        phoneNumber: payload.phone_number,
      });
      return {
        success: response.success,
        message: response.data.message,
        phone_number: response.data.phone_number,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getPhoneNumber: IGetPhoneNumberService = async () => {
    const response = await smsRepository.getPhoneNumber();
    return {
      success: true,
      data: {
        phoneNumber: response.phoneNumber,
        isVerified: response.isVerified,
      },
    };
  };

  const bindTexting: IBindTextingService = async ({
    phone_number,
    clinic_id,
  }) => {
    try {
      const response = await smsRepository.bindTexting({
        phoneNumber: phone_number,
        clinicId: clinic_id,
      });
      if (!response.status) {
        return {
          success: false,
          error: {
            message: response.message || "We couldn't enable texting.",
          },
        };
      }
      return {
        success: true,
        data: {
          status: response.binding.status,
          smsSent: response.binding.sms_sent,
          message: response.message,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getTextingStatus: IGetTextingStatusService = async () => {
    try {
      const response = await smsRepository.getTextingStatus();
      const binding = response.binding;
      return {
        success: true,
        data: {
          phone: binding?.phone ?? "",
          status: binding?.status ?? "none",
          clinicId: binding?.clinic_id ?? null,
          verifiedAt: binding?.verified_at ?? null,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const revokeTexting: IRevokeTextingService = async ({ phone_number }) => {
    try {
      const response = await smsRepository.revokeTexting({
        phoneNumber: phone_number,
      });
      // The transport resolves a failed request with its error body (FastAPI
      // `{ detail }`), so require the route's `{ status: true }` success shape.
      if ((response as { status?: unknown } | null)?.status !== true) {
        const detail = (response as { detail?: unknown } | null)?.detail;
        return {
          success: false,
          error: {
            message:
              typeof detail === "string"
                ? detail
                : "We couldn't turn off texting.",
          },
        };
      }
      return { success: true, data: null };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  return {
    sendOTP,
    verifyOTP,
    savePhoneNumber,
    getPhoneNumber,
    bindTexting,
    getTextingStatus,
    revokeTexting,
  };
};
